import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { useFormContext } from "react-hook-form";

const labelStyle = "block text-sm font-medium text-gray-300 mb-2";
const inputStyle =
  "w-full px-4 py-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-800 text-gray-200";

function FormStep1({ copy, showErrors }) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const firstNameProps = register("firstName", {
    required: copy.errorMessages.required,
  });
  const lastNameProps = register("lastName", {
    required: copy.errorMessages.required,
  });
  const emailProps = register("email", {
    required: copy.errorMessages.required,
    pattern: {
      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: copy.errorMessages.invalidEmail,
    },
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="firstName" className={labelStyle}>
            {copy.fieldLabels.firstName}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <motion.input
            type="text"
            id="firstName"
            name={firstNameProps.name}
            onChange={firstNameProps.onChange}
            onBlur={firstNameProps.onBlur}
            ref={firstNameProps.ref}
            className={`${inputStyle} ${showErrors && errors.firstName ? "border-red-500" : ""}`}
            placeholder={copy.placeholders.firstName}
            whileFocus={{ scale: 1.01 }}
          />
          {showErrors && errors.firstName && (
            <p className="mt-1 text-sm text-red-500">
              {errors.firstName.message}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="lastName" className={labelStyle}>
            {copy.fieldLabels.lastName}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <motion.input
            type="text"
            id="lastName"
            name={lastNameProps.name}
            onChange={lastNameProps.onChange}
            onBlur={lastNameProps.onBlur}
            ref={lastNameProps.ref}
            className={`${inputStyle} ${showErrors && errors.lastName ? "border-red-500" : ""}`}
            placeholder={copy.placeholders.lastName}
            whileFocus={{ scale: 1.01 }}
          />
          {showErrors && errors.lastName && (
            <p className="mt-1 text-sm text-red-500">
              {errors.lastName.message}
            </p>
          )}
        </div>
      </div>
      <div>
        <label htmlFor="email" className={labelStyle}>
          {copy.fieldLabels.email}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <motion.input
          type="email"
          id="email"
          name={emailProps.name}
          onChange={emailProps.onChange}
          onBlur={emailProps.onBlur}
          ref={emailProps.ref}
          className={`${inputStyle} ${showErrors && errors.email ? "border-red-500" : ""}`}
          placeholder={copy.placeholders.email}
          whileFocus={{ scale: 1.01 }}
        />
        {showErrors && errors.email && (
          <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
        )}
      </div>
    </div>
  );
}

FormStep1.propTypes = {
  copy: PropTypes.shape({
    fieldLabels: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }).isRequired,
    errorMessages: PropTypes.shape({
      required: PropTypes.string.isRequired,
      invalidEmail: PropTypes.string.isRequired,
    }).isRequired,
    placeholders: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  showErrors: PropTypes.bool.isRequired,
};

export default React.memo(FormStep1);
